'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import SeniorsWalkthrough from '@/components/SeniorsWalkthrough';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { trackSubscriptionCtaClicked, trackRegionPaymentClicked, type SeniorsRegion,  } from '@/lib/analytics/seniorsTracking';
import { getTranslations, type Language, SUPPORTED_LANGUAGES } from '@/lib/i18n';

// ─── Types ────────────────────────────────────────────────────────────────────
interface PricingTier {
  id: string;
  name: string;
  audience: string;
  price: number;
  period: string;
  description: string;
  features: string[];
  highlight: boolean;
  badge?: string;
  ctaLabel: string;
}

interface FeaturePillar {
  id: string;
  icon: string;
  title: string;
  subtitle: string;
  description: string;
  tags: string[];
  span?: 'wide' | 'tall' | 'normal';
}

// ─── Data ─────────────────────────────────────────────────────────────────────
const pricingTiers: PricingTier[] = [
  {
    id: 'student',
    name: 'Student',
    audience: 'For secondary & high school students',
    price: 15,
    period: '/month',
    description: 'Build daily discipline, manage studies, and grow spiritually with AI guidance tailored for student life.',
    features: [
      'AI life coaching (text)',
      'Daily habit tracker',
      'Prayer time reminders',
      'Sabr & Shukr journal',
      'Weekly progress report',
      'English + Arabic support',
    ],
    highlight: false,
    ctaLabel: 'Start as Student',
  },
  {
    id: 'undergraduate',
    name: 'Undergraduate',
    audience: 'For university & college students',
    price: 20,
    period: '/month',
    description: 'Navigate university pressures, career planning, and personal growth with advanced AI coaching and research tools.',
    features: [
      'Everything in Student',
      'Voice AI coaching (Advanced)',
      'Academic Research Mode',
      'Career Launchpad access',
      'Multi-language support (9 langs)',
      'Ambassador referral rewards',
    ],
    highlight: true,
    badge: 'Most Popular',
    ctaLabel: 'Start as Undergraduate',
  },
  {
    id: 'seniors',
    name: 'Seniors',
    audience: 'For Muslim seniors & elders',
    price: 25,
    period: '/month',
    description: 'Connect with verified Islamic scholars for personal answers to your questions — with the patience, respect, and depth that seniors deserve.',
    features: [
      'Everything in Undergraduate',
      'Scholar Directory access',
      'Personal scholar Q&A',
      'Verified sheikh responses',
      'Senior-friendly interface',
      'Priority support',
    ],
    highlight: false,
    badge: 'For Seniors',
    ctaLabel: 'Start as Senior',
  },
  {
    id: 'premium',
    name: 'Premium',
    audience: 'For entrepreneurs & professionals',
    price: 30,
    period: '/month',
    description: 'Full-spectrum AI ecosystem for business growth, spiritual leadership, and high-performance life management.',
    features: [
      'Everything in Undergraduate',
      'Business strategy coaching',
      'Rafeeq Academic Research Assistant',
      'Priority voice response',
      'Custom AI persona tuning',
      'Dedicated onboarding call',
    ],
    highlight: false,
    badge: 'Enterprise',
    ctaLabel: 'Start Premium',
  },
];

const featurePillars: FeaturePillar[] = [
  {
    id: 'coaching',
    icon: '🧠',
    title: 'Three-Pillar AI Coaching',
    subtitle: 'Psychology meets Islamic wisdom',
    description: 'Rafeeq synthesizes Cognitive Behavioral frameworks with Sabr, Shukr, and Muhasabah — giving you a coach that understands both your mind and your deen.',
    tags: ['CBT Metrics', 'Resilience Tracking', 'Daily Reflection'],
    span: 'wide',
  },
  {
    id: 'voice',
    icon: '🎙️',
    title: 'Real-Time Voice AI',
    subtitle: 'Speak. Be heard. Grow.',
    description: 'Bi-directional voice coaching — ultra-low latency, warm tone, in your language.',
    tags: ['Voice AI', 'Rafeeq AI', 'Multilingual'],
    span: 'normal',
  },
  {
    id: 'research',
    icon: '📚',
    title: 'Academic Research Mode',
    subtitle: 'Cite-backed answers, instantly',
    description: 'Toggle on Rafeeq Academic Research Assistant for real-time academic citations — without losing Rafeeq\'s Islamic psychological voice.',
    tags: ['Rafeeq Academic Research Assistant', 'Live Citations', 'Research-Grade'],
    span: 'normal',
  },
  {
    id: 'diaspora',
    icon: '🌍',
    title: 'Global Muslim Diaspora',
    subtitle: '10 languages, one community',
    description: 'Built for Somali, Arab, South Asian, and Southeast Asian communities. English, Arabic, Somali, Urdu, Indonesian, Malay, French, Turkish, Swahili, Bengali — all supported natively.',
    tags: ['10 Languages', 'Cultural Context', 'Inclusive Fiqh'],
    span: 'wide',
  },
  {
    id: 'career',
    icon: '🚀',
    title: 'Career Launchpad',
    subtitle: 'From student to professional',
    description: 'Encrypted career pipeline connecting your verified professional profile to job matches — your spiritual journals stay private, always.',
    tags: ['Zero-Knowledge Privacy', 'Job Matching', 'Consent-First'],
    span: 'normal',
  },
  {
    id: 'security',
    icon: '🔐',
    title: 'Military-Grade Privacy',
    subtitle: 'Your data, your control',
    description: 'End-to-end encrypted chats, zero-knowledge local storage, WebAuthn passkey login, and one-click GDPR data purge.',
    tags: ['WebAuthn', 'E2E Encrypted', 'GDPR Compliant'],
    span: 'normal',
  },
];

// ─── Sub-components ────────────────────────────────────────────────────────────
function NavBar({ t }: { t: ReturnType<typeof getTranslations> }) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-card/90 backdrop-blur-md border-b border-border shadow-card'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <AppLogo size={32} />
          <span className="font-bold text-lg text-foreground tracking-tight">Rafeeq</span>
        </div>
        <nav className="hidden md:flex items-center gap-8">
          <a href="#features" className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-150">{t.nav_features}</a>
          <a href="#pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-150">{t.nav_pricing}</a>
          <Link href="/pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-150">{t.nav_all_plans}</Link>
          <a href="#community" className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-150">{t.nav_community}</a>
          <a href="#ambassadors" className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-150">{t.nav_ambassadors}</a>
          <a href="#consultants" className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-150">{t.nav_consultants}</a>
        </nav>
        <div className="flex items-center gap-3">
          <Link
            href="/sign-up-login-screen"
            className="text-sm font-500 text-muted-foreground hover:text-foreground transition-colors duration-150"
          >
            {t.nav_sign_in}
          </Link>
          <Link
            href="/sign-up-login-screen"
            className="text-sm font-600 px-4 py-2 rounded-xl gradient-teal text-white shadow-prayer hover:opacity-90 transition-opacity duration-150"
          >
            {t.nav_get_started}
          </Link>
        </div>
      </div>
    </header>
  );
}

function InstallWebAppButton() {
  const [deferredPrompt, setDeferredPrompt] = useState<Event | null>(null);
  const [canInstall, setCanInstall] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setCanInstall(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Check if already installed (standalone mode)
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setInstalled(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      (deferredPrompt as any).prompt();
      const { outcome } = await (deferredPrompt as any).userChoice;
      if (outcome === 'accepted') {
        setInstalled(true);
        setCanInstall(false);
      }
      setDeferredPrompt(null);
    } else {
      // Fallback: guide user to install manually
      alert('To install: open your browser menu and select "Add to Home Screen" or "Install App".');
    }
  };

  if (installed) return null;

  return (
    <button
      onClick={handleInstall}
      aria-label="Install Rafeeq as a Web App"
      className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-xl bg-primary/10 border border-primary/30 text-primary hover:bg-primary/20 transition-all duration-150 shadow-card"
    >
      <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 8h6m-5 0V6a2 2 0 012-2h2a2 2 0 012 2v2m-6 0H7a2 2 0 00-2 2v9a2 2 0 002 2h10a2 2 0 002-2v-9a2 2 0 00-2-2h-2M12 12v5m0 0l-2-2m2 2l2-2" />
      </svg>
      <div className="text-left leading-tight">
        <div className="text-[10px] opacity-75">Install as</div>
        <div className="text-sm font-600">Web App</div>
      </div>
    </button>
  );
}

function HeroSection({ t }: { t: ReturnType<typeof getTranslations> }) {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
      {/* Background blobs */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 blob-teal opacity-60" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 blob-gold opacity-50" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-primary/5 to-accent/5 blur-3xl" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-500 mb-8">
          <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
          {t.hero_badge}
        </div>

        {/* Headline */}
        <h1 className="text-hero-xl text-foreground mb-6 max-w-4xl mx-auto">
          {t.hero_headline_1}{' '}
          <span className="relative inline-block">
            <span className="relative z-10 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              {t.hero_headline_highlight}
            </span>
            <span className="absolute bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-primary/40 to-accent/40 rounded-full" />
          </span>
          {' '}{t.hero_headline_2}
        </h1>

        {/* Sub-headline */}
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
          {t.hero_subheadline}
        </p>

        {/* CTA cluster */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
          <Link
            href="/sign-up-login-screen"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl gradient-teal text-white font-600 text-base shadow-prayer hover:opacity-90 transition-all duration-200 hover:scale-[1.02]"
          >
            {t.hero_cta_primary}
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
            </svg>
          </Link>
          <a
            href="#pricing"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl bg-card border border-border text-foreground font-500 text-base hover:bg-muted transition-colors duration-150"
          >
            {t.hero_cta_secondary}
          </a>
        </div>

        {/* Download App Links */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-10">
          <p className="text-sm text-muted-foreground font-500 sm:mr-2">{t.hero_download_label}</p>
          <a
            href="#"
            aria-label="Download Rafeeq on the App Store"
            className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-xl bg-foreground text-background hover:opacity-85 transition-opacity duration-150 shadow-card"
          >
            <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
            <div className="text-left leading-tight">
              <div className="text-[10px] opacity-75">Download on the</div>
              <div className="text-sm font-600">App Store</div>
            </div>
          </a>
          <a
            href="#"
            aria-label="Get Rafeeq on Google Play"
            className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-xl bg-foreground text-background hover:opacity-85 transition-opacity duration-150 shadow-card"
          >
            <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3.18 23.76c.3.17.64.24.99.2l12.6-7.27-2.72-2.72-10.87 9.79zM.5 1.4C.19 1.74 0 2.28 0 2.98v18.04c0 .7.19 1.24.51 1.58l.08.08 10.1-10.1v-.24L.58 1.32.5 1.4zM20.49 10.37l-2.72-1.57-3.04 3.04 3.04 3.04 2.74-1.58c.78-.45.78-1.48-.02-1.93zM4.17.24L16.77 7.5l-2.72 2.72L3.18.44c.3-.17.65-.23.99-.2z"/>
            </svg>
            <div className="text-left leading-tight">
              <div className="text-[10px] opacity-75">Get it on</div>
              <div className="text-sm font-600">Google Play</div>
            </div>
          </a>
          <InstallWebAppButton />
        </div>

        {/* Trust indicators */}
        <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
          {[
            { icon: '🔐', text: t.hero_trust_encrypted },
            { icon: '🌍', text: t.hero_trust_languages },
            { icon: '🕌', text: t.hero_trust_islamic },
            { icon: '⚡', text: t.hero_trust_voice },
          ].map((item) => (
            <div key={item.text} className="flex items-center gap-1.5">
              <span>{item.icon}</span>
              <span>{item.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-muted-foreground/60">
        <span className="text-xs tracking-widest uppercase">{t.hero_explore}</span>
        <div className="w-px h-8 bg-gradient-to-b from-muted-foreground/40 to-transparent" />
      </div>
    </section>
  );
}

function FeatureCard({ pillar }: { pillar: FeaturePillar }) {
  return (
    <div
      className={`relative group bg-card border border-border rounded-2xl p-6 hover:shadow-card-hover transition-all duration-300 hover:-translate-y-0.5 overflow-hidden ${
        pillar.span === 'wide' ? 'md:col-span-2' : ''
      }`}
    >
      {/* Subtle gradient overlay on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/0 to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl pointer-events-none" />

      <div className="relative z-10">
        <div className="text-3xl mb-4">{pillar.icon}</div>
        <p className="text-xs font-600 text-primary uppercase tracking-widest mb-1">{pillar.subtitle}</p>
        <h3 className="text-lg font-700 text-foreground mb-3">{pillar.title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed mb-4">{pillar.description}</p>
        <div className="flex flex-wrap gap-2">
          {pillar.tags.map((tag) => (
            <span
              key={tag}
              className="text-xs px-2.5 py-1 rounded-full bg-muted text-muted-foreground border border-border font-500"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function FeaturesSection({ t }: { t: ReturnType<typeof getTranslations> }) {
  return (
    <section id="features" className="py-24 px-6">
      <div className="max-w-6xl mx-auto">
        {/* Section header */}
        <div className="max-w-2xl mb-16">
          <p className="text-sm font-600 text-primary uppercase tracking-widest mb-3">{t.features_label}</p>
          <h2 className="text-4xl font-800 text-foreground mb-4 leading-tight">
            {t.features_heading_1}<br />
            <span className="text-muted-foreground font-500">{t.features_heading_2}</span>
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            {t.features_subtext}
          </p>
        </div>

        {/* Bento grid — asymmetric, anti-slop */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {featurePillars.map((pillar) => (
            <FeatureCard key={pillar.id} pillar={pillar} />
          ))}
        </div>
      </div>
    </section>
  );
}

function PricingCard({ tier, index, t }: { tier: PricingTier; index: number; t: ReturnType<typeof getTranslations> }) {
  const handleCtaClick = () => {
    trackSubscriptionCtaClicked({
      tier_id: tier.id,
      tier_name: tier.name,
      cta_label: tier.ctaLabel,
      source: 'pricing_section',
    });
  };

  return (
    <div
      className={`relative flex flex-col rounded-2xl border transition-all duration-300 hover:-translate-y-1 hover:shadow-card-lg ${
        tier.highlight
          ? 'border-primary bg-gradient-to-b from-primary/5 to-card shadow-prayer'
          : 'border-border bg-card shadow-card'
      }`}
    >
      {tier.badge && (
        <div
          className={`absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-700 tracking-wide ${
            tier.highlight
              ? 'gradient-teal text-white shadow-prayer'
              : 'bg-accent/10 text-accent border border-accent/20'
          }`}
        >
          {tier.badge}
        </div>
      )}

      <div className="p-8 flex-1">
        {/* Tier header */}
        <div className="mb-6">
          <h3 className="text-xl font-700 text-foreground mb-1">{tier.name}</h3>
          <p className="text-xs text-muted-foreground">{tier.audience}</p>
        </div>

        {/* Price */}
        <div className="flex items-end gap-1 mb-2">
          <span className="text-4xl font-800 text-foreground">${tier.price}</span>
          <span className="text-muted-foreground text-sm mb-1.5">{tier.period}</span>
        </div>
        <p className="text-xs text-muted-foreground mb-1 italic">{t.pricing_usd_note}</p>
        <p className="text-sm text-muted-foreground mb-6 leading-relaxed">{tier.description}</p>

        {/* Features list */}
        <ul className="space-y-3 mb-8">
          {tier.features.map((feature) => (
            <li key={feature} className="flex items-start gap-2.5 text-sm text-foreground">
              <svg
                className={`w-4 h-4 mt-0.5 flex-shrink-0 ${tier.highlight ? 'text-primary' : 'text-accent'}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
              {feature}
            </li>
          ))}
        </ul>
      </div>

      {/* CTA — routes to sign-up; actual payment handled on regional pricing pages */}
      <div className="px-8 pb-8">
        <Link
          href="/sign-up-login-screen"
          onClick={handleCtaClick}
          className={`block w-full text-center py-3 rounded-xl font-600 text-sm transition-all duration-200 hover:scale-[1.02] ${
            tier.highlight
              ? 'gradient-teal text-white shadow-prayer hover:opacity-90'
              : 'bg-muted text-foreground border border-border hover:bg-secondary'
          }`}
        >
          {tier.ctaLabel}
        </Link>
        <p className="text-center text-xs text-muted-foreground mt-2">
          {t.pricing_then_choose}
        </p>
      </div>
    </div>
  );
}

// ─── Regional payment links block ─────────────────────────────────────────────
const regionalPayments: Array<{
  country: string;
  flag: string;
  currency: string;
  method: string;
  href: string;
  region: SeniorsRegion;
  color: string;
  labelColor: string;
  badgeColor: string;
}> = [
  {
    country: 'Kenya',
    flag: '🇰🇪',
    currency: 'KES',
    method: 'M-Pesa',
    href: '/pricing-kenya',
    region: 'kenya',
    color: 'bg-green-50 border-green-200 hover:bg-green-100',
    labelColor: 'text-green-800',
    badgeColor: 'bg-green-100 text-green-700',
  },
  {
    country: 'Uganda',
    flag: '🇺🇬',
    currency: 'UGX',
    method: 'MTN MoMo / Airtel Money',
    href: '/pricing-uganda',
    region: 'uganda',
    color: 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100',
    labelColor: 'text-yellow-900',
    badgeColor: 'bg-yellow-100 text-yellow-700',
  },
  {
    country: 'Somalia',
    flag: '🇸🇴',
    currency: 'SOS',
    method: 'Hormuud EVC Plus / Somtel eDahab',
    href: '/pricing-somalia',
    region: 'somalia',
    color: 'bg-sky-50 border-sky-200 hover:bg-sky-100',
    labelColor: 'text-sky-800',
    badgeColor: 'bg-sky-100 text-sky-700',
  },
  {
    country: 'Indonesia',
    flag: '🇮🇩',
    currency: 'IDR',
    method: 'GoPay / OVO / DANA',
    href: '/pricing-indonesia',
    region: 'indonesia',
    color: 'bg-red-50 border-red-200 hover:bg-red-100',
    labelColor: 'text-red-800',
    badgeColor: 'bg-red-100 text-red-700',
  },
  {
    country: 'Pakistan',
    flag: '🇵🇰',
    currency: 'PKR',
    method: 'JazzCash / EasyPaisa',
    href: '/pricing-pakistan',
    region: 'pakistan',
    color: 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100',
    labelColor: 'text-emerald-800',
    badgeColor: 'bg-emerald-100 text-emerald-700',
  },
  {
    country: 'Malaysia',
    flag: '🇲🇾',
    currency: 'MYR',
    method: "Touch 'n Go / Boost / GrabPay",
    href: '/pricing-malaysia',
    region: 'malaysia',
    color: 'bg-blue-50 border-blue-200 hover:bg-blue-100',
    labelColor: 'text-blue-800',
    badgeColor: 'bg-blue-100 text-blue-700',
  },
];

function RegionalPaymentLinks({ t }: { t: ReturnType<typeof getTranslations> }) {
  const handleRegionClick = (region: typeof regionalPayments[0]) => {
    trackRegionPaymentClicked({
      region: region.region,
      country: region.country,
      currency: region.currency,
      payment_method: region.method,
      source: 'pricing_section',
    });
  };

  return (
    <div className="mt-12 rounded-2xl border border-border bg-card p-8 shadow-card">
      <div className="text-center mb-6">
        <p className="text-sm font-600 text-primary uppercase tracking-widest mb-2">{t.pricing_regional_label}</p>
        <h3 className="text-xl font-700 text-foreground mb-1">{t.pricing_regional_heading}</h3>
        <p className="text-sm text-muted-foreground">
          {t.pricing_regional_subtext}
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        {regionalPayments.map((region) => (
          <Link
            key={region.country}
            href={region.href}
            onClick={() => handleRegionClick(region)}
            className={`flex flex-col items-center gap-2 p-4 rounded-xl border transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${region.color}`}
          >
            <span className="text-3xl">{region.flag}</span>
            <span className={`font-700 text-sm ${region.labelColor}`}>{region.country}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-600 ${region.badgeColor}`}>{region.currency}</span>
            <span className={`text-xs text-center leading-tight ${region.labelColor} opacity-80`}>{region.method}</span>
            <span className={`text-xs font-600 mt-1 ${region.labelColor} flex items-center gap-1`}>
              {t.pricing_pay_here}
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
            </span>
          </Link>
        ))}
      </div>
      <p className="text-center text-xs text-muted-foreground mt-4">
        {t.pricing_no_card}
      </p>
    </div>
  );
}

function PricingSection({ t }: { t: ReturnType<typeof getTranslations> }) {
  return (
    <section id="pricing" className="py-24 px-6 bg-muted/40">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-sm font-600 text-primary uppercase tracking-widest mb-3">{t.pricing_label}</p>
          <h2 className="text-4xl font-800 text-foreground mb-4 leading-tight">
            {t.pricing_heading}
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            {t.pricing_subtext}
          </p>
        </div>

        {/* Pricing grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          {pricingTiers.map((tier, index) => (
            <PricingCard key={tier.id} tier={tier} index={index} t={t} />
          ))}
        </div>

        {/* Regional mobile money payment links */}
        <RegionalPaymentLinks t={t} />

        {/* PPP note */}
        <div className="mt-6 text-center">
          <p className="text-sm text-muted-foreground">
            {t.pricing_ppp_note}{' '}
            <Link href="/pricing-kenya" className="text-primary hover:underline">Kenya →</Link>{' '}
            <Link href="/pricing-uganda" className="text-primary hover:underline ml-2">Uganda →</Link>{' '}
            <Link href="/pricing-somalia" className="text-primary hover:underline ml-2">Somalia →</Link>{' '}
            <Link href="/pricing-indonesia" className="text-primary hover:underline ml-2">Indonesia →</Link>{' '}
            <Link href="/pricing-pakistan" className="text-primary hover:underline ml-2">Pakistan →</Link>{' '}
            <Link href="/pricing-malaysia" className="text-primary hover:underline ml-2">Malaysia →</Link>
          </p>
        </div>
      </div>
    </section>
  );
}

function CommunitySection({ t }: { t: ReturnType<typeof getTranslations> }) {
  const stats = [
    { value: '10', label: t.community_stat_languages, icon: '🌐' },
    { value: '3', label: t.community_stat_pillars, icon: '🧠' },
    { value: '∞', label: t.community_stat_growth, icon: '📈' },
    { value: '100%', label: t.community_stat_privacy, icon: '🔐' },
  ];

  return (
    <section id="community" className="py-24 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: text */}
          <div>
            <p className="text-sm font-600 text-primary uppercase tracking-widest mb-3">{t.community_label}</p>
            <h2 className="text-4xl font-800 text-foreground mb-6 leading-tight">
              {t.community_heading_1}<br />
              <span className="text-muted-foreground font-500">{t.community_heading_2}</span>
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-8">
              {t.community_subtext}
            </p>
            <Link
              href="/sign-up-login-screen"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl gradient-teal text-white font-600 text-sm shadow-prayer hover:opacity-90 transition-opacity duration-150"
            >
              {t.community_cta}
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
            </Link>
          </div>

          {/* Right: stats bento */}
          <div className="grid grid-cols-2 gap-4">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="bg-card border border-border rounded-2xl p-6 shadow-card hover:shadow-card-hover transition-shadow duration-200"
              >
                <div className="text-2xl mb-3">{stat.icon}</div>
                <div className="text-3xl font-800 text-foreground mb-1">{stat.value}</div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Community feature highlights */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              icon: '🏆',
              title: 'Prayer Leaderboard',
              desc: 'Compete with friends on prayer consistency, streaks, and daily habits. Healthy accountability in a supportive environment.',
              href: '/prayer-leaderboard',
              cta: 'View Leaderboard',
            },
            {
              icon: '🎯',
              title: 'Shared Goals & Challenges',
              desc: 'Join community challenges — 30-day Quran reading, Fajr streaks, gratitude journals — and celebrate wins together.',
              href: '/challenges',
              cta: 'Browse Challenges',
            },
            {
              icon: '💬',
              title: 'Prayer Groups',
              desc: 'Create or join private prayer groups with family, friends, or your local masjid community for shared accountability.',
              href: '/prayer-groups',
              cta: 'Join a Group',
            },
          ].map((item) => (
            <div
              key={item.title}
              className="bg-card border border-border rounded-2xl p-6 shadow-card hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-200"
            >
              <div className="text-3xl mb-4">{item.icon}</div>
              <h3 className="text-base font-700 text-foreground mb-2">{item.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">{item.desc}</p>
              <Link
                href={item.href}
                className="inline-flex items-center gap-1.5 text-sm font-600 text-primary hover:underline"
              >
                {item.cta}
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ConsultantsSection({ t }: { t: ReturnType<typeof getTranslations> }) {
  const consultants = [
    {
      name: 'Dr. Amina Hassan',
      specialty: 'Life & Career Coach',
      flag: '🇸🇴',
      rating: 4.9,
      sessions: 320,
      tags: ['Career', 'Mindset', 'Productivity'],
      available: true,
    },
    {
      name: 'Br. Tariq Mahmood',
      specialty: 'Business Strategy',
      flag: '🇵🇰',
      rating: 4.8,
      sessions: 215,
      tags: ['Entrepreneurship', 'Finance', 'Halal Business'],
      available: true,
    },
    {
      name: 'Sr. Fatima Al-Zahra',
      specialty: 'Mental Wellness',
      flag: '🇲🇾',
      rating: 5.0,
      sessions: 180,
      tags: ['Anxiety', 'Relationships', 'Self-Care'],
      available: false,
    },
  ];

  return (
    <section id="consultants" className="py-24 px-6 bg-muted/30">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-14">
          <div>
            <p className="text-sm font-600 text-primary uppercase tracking-widest mb-3">{t.consultants_label}</p>
            <h2 className="text-4xl font-800 text-foreground mb-5 leading-tight">
              {t.consultants_heading_1}<br />
              <span className="text-muted-foreground font-500">{t.consultants_heading_2}</span>
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-6">
              {t.consultants_subtext}
            </p>
            <div className="space-y-3 mb-8">
              {[
                { icon: '✅', text: 'All consultants are background-checked and reviewed' },
                { icon: '🎥', text: 'Secure HD video sessions with end-to-end encryption' },
                { icon: '💳', text: 'Pay per session — no subscription required' },
                { icon: '⭐', text: 'Verified ratings from real clients after every session' },
              ].map((item) => (
                <div key={item.text} className="flex items-start gap-3">
                  <span className="text-base mt-0.5">{item.icon}</span>
                  <span className="text-sm text-foreground">{item.text}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                href="/browse-consultants"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl gradient-teal text-white font-700 text-sm shadow-prayer hover:opacity-90 transition-all duration-200 hover:scale-[1.02]"
              >
                {t.consultants_cta_browse}
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </Link>
              <Link
                href="/consultant-onboarding"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-card border border-border text-foreground font-500 text-sm hover:bg-muted transition-colors duration-150"
              >
                {t.consultants_cta_become}
              </Link>
            </div>
          </div>

          {/* Right: how it works */}
          <div className="grid grid-cols-1 gap-4">
            {[
              { step: '01', title: 'Browse & Filter', desc: 'Search by specialty, language, availability, and price. Read verified reviews before booking.' },
              { step: '02', title: 'Book a Session', desc: 'Pick a time slot that works for you. Pay securely via card or mobile money.' },
              { step: '03', title: 'Connect & Grow', desc: 'Join your private video session. Receive follow-up notes and action steps from your consultant.' },
            ].map((step) => (
              <div key={step.step} className="flex items-start gap-4 bg-card border border-border rounded-2xl p-5 shadow-card">
                <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-800 text-primary">{step.step}</span>
                </div>
                <div>
                  <h4 className="text-sm font-700 text-foreground mb-1">{step.title}</h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Consultant preview cards */}
        <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="text-xs font-600 text-primary uppercase tracking-widest mb-1">{t.consultants_featured_label}</p>
              <h3 className="text-lg font-700 text-foreground">{t.consultants_featured_heading}</h3>
            </div>
            <Link
              href="/browse-consultants"
              className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-primary/10 text-primary text-xs font-700 hover:bg-primary/20 transition-colors border border-primary/20"
            >
              {t.consultants_view_all}
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {consultants.map((c) => (
              <div key={c.name} className="flex flex-col gap-3 p-4 rounded-xl bg-muted/40 border border-border hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-200">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center text-xl flex-shrink-0">
                    {c.flag}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-700 text-foreground truncate">{c.name}</p>
                    <p className="text-xs text-muted-foreground">{c.specialty}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="text-amber-500">★</span>
                  <span className="font-600 text-foreground">{c.rating}</span>
                  <span>·</span>
                  <span>{c.sessions} {t.consultants_sessions}</span>
                  <span className={`ml-auto w-1.5 h-1.5 rounded-full ${c.available ? 'bg-emerald-400' : 'bg-amber-400'}`} />
                  <span>{c.available ? t.consultants_available : t.consultants_busy}</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {c.tags.map((tag) => (
                    <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-muted border border-border text-muted-foreground font-500">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function AmbassadorsSection({ t }: { t: ReturnType<typeof getTranslations> }) {
  const tiers = [
    { name: 'Starter', commission: '15%', referrals: '1–9', color: 'bg-muted/60 border-border', badge: '' },
    { name: 'Silver', commission: '20%', referrals: '10–49', color: 'bg-card border-primary/30', badge: 'Popular' },
    { name: 'Gold', commission: '25%', referrals: '50–199', color: 'bg-accent/5 border-accent/30', badge: '' },
    { name: 'Platinum', commission: '30%', referrals: '200+', color: 'bg-primary/5 border-primary/40', badge: 'Top Tier' },
  ];

  return (
    <section id="ambassadors" className="py-24 px-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-sm font-600 text-primary uppercase tracking-widest mb-3">{t.ambassadors_label}</p>
          <h2 className="text-4xl font-800 text-foreground mb-4 leading-tight">
            {t.ambassadors_heading_1}<br />
            <span className="text-muted-foreground font-500">{t.ambassadors_heading_2}</span>
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            {t.ambassadors_subtext}
          </p>
        </div>

        {/* Tier cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {tiers.map((tier) => (
            <div
              key={tier.name}
              className={`relative rounded-2xl border p-5 text-center shadow-card hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-200 ${tier.color}`}
            >
              {tier.badge && (
                <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-[10px] font-700 gradient-teal text-white shadow-prayer">
                  {tier.badge}
                </div>
              )}
              <p className="text-base font-800 text-foreground mb-1">{tier.name}</p>
              <p className="text-3xl font-800 text-primary mb-1">{tier.commission}</p>
              <p className="text-xs text-muted-foreground">{t.ambassadors_commission}</p>
              <div className="mt-3 pt-3 border-t border-border">
                <p className="text-xs text-muted-foreground">{tier.referrals} {t.ambassadors_referrals}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Benefits grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {[
            { icon: '💸', title: 'Recurring Commissions', desc: 'Earn every month for as long as your referrals stay subscribed — not just on the first payment.' },
            { icon: '📊', title: 'Real-Time Dashboard', desc: 'Track clicks, conversions, earnings, and tier progress in your personal ambassador dashboard.' },
            { icon: '🔗', title: 'Custom Referral Links', desc: 'Get a personalised referral link and QR code to share on social media, WhatsApp, or your website.' },
            { icon: '💳', title: 'Flexible Payouts', desc: 'Withdraw earnings via M-Pesa, MTN MoMo, bank transfer, or card — in your local currency.' },
            { icon: '🏅', title: 'Tier Bonuses', desc: 'Unlock bonus payouts, exclusive features, and priority support as you climb the tier ladder.' },
            { icon: '🤝', title: 'Community of Ambassadors', desc: 'Join a private group of top ambassadors for tips, resources, and collaborative growth strategies.' },
          ].map((item) => (
            <div
              key={item.title}
              className="bg-card border border-border rounded-2xl p-5 shadow-card hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-200"
            >
              <div className="text-2xl mb-3">{item.icon}</div>
              <h3 className="text-sm font-700 text-foreground mb-2">{item.title}</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>

        {/* CTA banner */}
        <div className="rounded-2xl border border-primary/20 bg-primary/5 p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-xl font-800 text-foreground mb-2">{t.ambassadors_ready_heading}</h3>
            <p className="text-sm text-muted-foreground max-w-lg">
              {t.ambassadors_ready_subtext}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <Link
              href="/ambassador/apply"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl gradient-teal text-white font-700 text-sm shadow-prayer hover:opacity-90 transition-all duration-200 hover:scale-[1.02]"
            >
              {t.ambassadors_cta_apply}
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
            </Link>
            <Link
              href="/home/ambassador-program"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-card border border-border text-foreground font-500 text-sm hover:bg-muted transition-colors duration-150"
            >
              {t.ambassadors_cta_learn}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function OnboardingCTA({ t }: { t: ReturnType<typeof getTranslations> }) {
  return (
    <section className="py-24 px-6">
      <div className="max-w-4xl mx-auto">
        <div className="relative rounded-3xl overflow-hidden gradient-teal p-12 text-center shadow-prayer">
          {/* Background texture */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-white blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-white blur-2xl" />
          </div>

          <div className="relative z-10">
            <p className="text-white/70 text-sm font-600 uppercase tracking-widest mb-4">{t.onboarding_label}</p>
            <h2 className="text-3xl md:text-4xl font-800 text-white mb-4 leading-tight">
              {t.onboarding_heading}
            </h2>
            <p className="text-white/80 max-w-xl mx-auto mb-8 leading-relaxed">
              {t.onboarding_subtext}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                href="/sign-up-login-screen"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl bg-white text-primary font-700 text-base hover:bg-primary-foreground transition-colors duration-150 shadow-lg"
              >
                {t.onboarding_cta_create}
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </Link>
              <Link
                href="/sign-up-login-screen"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl border border-white/30 text-white font-500 text-base hover:bg-white/10 transition-colors duration-150"
              >
                {t.onboarding_cta_signin}
              </Link>
            </div>
            <p className="text-white/50 text-xs mt-6">
              {t.onboarding_privacy_note}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer({ t }: { t: ReturnType<typeof getTranslations> }) {
  return (
    <footer className="border-t border-border bg-card py-12 px-6">
      <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-start justify-between gap-8">
        <div className="flex items-center gap-2.5">
          <AppLogo size={28} />
          <span className="font-bold text-base text-foreground">Rafeeq</span>
        </div>
        <div className="flex flex-wrap gap-8 text-sm text-muted-foreground">
          <a href="#features" className="hover:text-foreground transition-colors">{t.footer_features}</a>
          <a href="#pricing" className="hover:text-foreground transition-colors">{t.footer_pricing}</a>
          <Link href="/home/ambassador-program" className="hover:text-foreground transition-colors">{t.footer_ambassadors}</Link>
          <Link href="/sign-up-login-screen" className="hover:text-foreground transition-colors">{t.footer_signin}</Link>
          <Link href="/pricing-kenya" className="hover:text-foreground transition-colors">Kenya Pricing (KES)</Link>
          <Link href="/pricing-uganda" className="hover:text-foreground transition-colors">Uganda Pricing (UGX)</Link>
          <Link href="/pricing-somalia" className="hover:text-foreground transition-colors">Somalia Pricing (SOS)</Link>
          <Link href="/pricing-indonesia" className="hover:text-foreground transition-colors">Indonesia Pricing (IDR)</Link>
          <Link href="/pricing-pakistan" className="hover:text-foreground transition-colors">Pakistan Pricing (PKR)</Link>
          <Link href="/pricing-malaysia" className="hover:text-foreground transition-colors">Malaysia Pricing (MYR)</Link>
        </div>
        <p className="text-xs text-muted-foreground">© 2026 Rafeeq. All rights reserved.</p>
      </div>
    </footer>
  );
}

// ─── Seniors Section ──────────────────────────────────────────────────────────
function SeniorsSection({ onStartTour, t }: { onStartTour: () => void; t: ReturnType<typeof getTranslations> }) {
  const features = [
    { icon: '🕌', title: 'Fiqh & Ibadah', desc: 'Questions about prayer, wudu, fasting, and worship for seniors with health considerations.' },
    { icon: '📖', title: 'Quran & Tafsir', desc: 'Guidance on recitation, understanding, and the spiritual meaning of verses.' },
    { icon: '🤲', title: 'Dua & Dhikr', desc: 'Authentic supplications and remembrance practices for daily spiritual nourishment.' },
    { icon: '⚖️', title: 'Aqeedah & Sunnah', desc: 'Clarifications on matters of faith and the prophetic tradition.' },
  ];

  return (
    <section id="seniors" className="py-24 px-6 bg-muted/30">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: text */}
          <div>
            <p className="text-sm font-600 text-primary uppercase tracking-widest mb-3">{t.seniors_label}</p>
            <h2 className="text-4xl font-800 text-foreground mb-5 leading-tight">
              {t.seniors_heading_1}<br />
              <span className="text-muted-foreground font-500">{t.seniors_heading_2}</span>
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-6">
              {t.seniors_subtext}
            </p>

            {/* Feature list */}
            <div className="space-y-3 mb-8">
              {[
                { icon: '✅', text: 'All scholars are verified and reviewed by our admin team' },
                { icon: '🔐', text: 'Your questions are private — never shared publicly' },
                { icon: '⏱️', text: 'Scholars respond within 12–48 hours' },
                { icon: '🌍', text: 'Scholars from Kenya, Somalia, Malaysia, Pakistan & more' },
              ].map((item) => (
                <div key={item.text} className="flex items-start gap-3">
                  <span className="text-base mt-0.5">{item.icon}</span>
                  <span className="text-sm text-foreground">{item.text}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={onStartTour}
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl gradient-teal text-white font-700 text-sm shadow-prayer hover:opacity-90 transition-all duration-200 hover:scale-[1.02]"
              >
                <span>✨</span>
                {t.seniors_cta_tour}
              </button>
              <Link
                href="/sign-up-login-screen"
                onClick={() =>
                  trackSubscriptionCtaClicked({
                    tier_id: 'seniors',
                    tier_name: 'Seniors',
                    cta_label: 'Browse Scholar Directory',
                    source: 'seniors_section',
                  })
                }
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-card border border-border text-foreground font-500 text-sm hover:bg-muted transition-colors duration-150"
              >
                {t.seniors_cta_browse}
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </Link>
            </div>
          </div>

          {/* Right: specialisation bento */}
          <div className="grid grid-cols-2 gap-4">
            {features.map((feat) => (
              <div
                key={feat.title}
                className="bg-card border border-border rounded-2xl p-5 hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-200"
              >
                <div className="text-2xl mb-3">{feat.icon}</div>
                <h4 className="text-sm font-700 text-foreground mb-1.5">{feat.title}</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Scholar preview strip */}
        <div className="mt-14 rounded-2xl border border-border bg-card p-6 shadow-card">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="text-xs font-600 text-primary uppercase tracking-widest mb-1">{t.seniors_featured_label}</p>
              <h3 className="text-lg font-700 text-foreground">{t.seniors_featured_heading}</h3>
            </div>
            <button
              onClick={onStartTour}
              className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-primary/10 text-primary text-xs font-700 hover:bg-primary/20 transition-colors border border-primary/20"
            >
              <span>✨</span> {t.seniors_take_tour}
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { name: 'Sheikh Ibrahim Al-Husseini', title: 'Fiqh & Ibadah Specialist', flag: '🇰🇪', answers: 1240, available: true, tags: ['Fiqh', 'Ibadah'] },
              { name: 'Sheikh Omar Faruq', title: 'Quran & Tafsir Scholar', flag: '🇸🇴', answers: 890, available: true, tags: ['Quran', 'Tafsir'] },
              { name: 'Sheikh Yusuf Al-Makki', title: 'Aqeedah & Sunnah Scholar', flag: '🇲🇾', answers: 670, available: false, tags: ['Aqeedah', 'Sunnah'] },
            ].map((s) => (
              <div key={s.name} className="flex items-start gap-3 p-3 rounded-xl bg-muted/40 border border-border">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center text-xl flex-shrink-0">
                  {s.flag}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="text-sm font-700 text-foreground truncate">{s.name}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-1.5">{s.title}</p>
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${s.available ? 'bg-emerald-400' : 'bg-amber-400'}`} />
                    <span className="text-[10px] text-muted-foreground">{s.available ? t.seniors_available : t.seniors_busy}</span>
                    <span className="text-[10px] text-muted-foreground ml-auto">{s.answers} {t.seniors_answers}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────
export default function HomePage() {
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [language, setLanguage] = useState<Language>('en');
  const t = getTranslations(language);

  const currentLangMeta = SUPPORTED_LANGUAGES.find(l => l.code === language);
  const dir = currentLangMeta?.dir || 'ltr';

  return (
    <div className="min-h-screen bg-background" dir={dir}>
      <NavBar t={t} />
      <LanguageSwitcher currentLanguage={language} onLanguageChange={setLanguage} />
      <main>
        <HeroSection t={t} />
        <FeaturesSection t={t} />
        <SeniorsSection onStartTour={() => setShowWalkthrough(true)} t={t} />
        <PricingSection t={t} />
        <ConsultantsSection t={t} />
        <AmbassadorsSection t={t} />
        <CommunitySection t={t} />
        <OnboardingCTA t={t} />
      </main>
      <Footer t={t} />
      {showWalkthrough && (
        <SeniorsWalkthrough onClose={() => setShowWalkthrough(false)} />
      )}
    </div>
  );
}
